import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../lib/utils';
import { 
  DollarSign, 
  TrendingUp, 
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const DashboardPaymentSummary = ({ stats }) => {
  // คำนวณยอดรวมทั้งหมด (รายได้จากลูกค้าเท่านั้น)
  const totalAmount = stats.totalRevenue;
  
  // คำนวณยอดที่ชำระแล้ว (รายได้จากลูกค้าที่ชำระแล้ว)
  const totalPaid = stats.totalPaid;
  
  // คำนวณยอดที่ค้างชำระ (รายได้จากลูกค้าที่ค้างชำระ)
  const totalPending = stats.pendingAmount;
  
  const paymentProgress = totalAmount > 0 ? Math.round((totalPaid / totalAmount) * 100) : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      {/* Payment Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <DollarSign className="h-5 w-5" />
            <span>สรุปสถานะการชำระเงิน</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2 mb-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">ยอดรวม</span>
              </div>
              <div className="text-2xl font-bold text-blue-800">
                {formatCurrency(totalAmount)}
              </div>
              <div className="text-xs text-blue-600 mt-1">
                รายได้จากลูกค้า
              </div>
            </div>
            
            <div className="p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium text-green-700">ชำระแล้ว</span>
              </div>
              <div className="text-2xl font-bold text-green-800">
                {formatCurrency(totalPaid)}
              </div>
              <div className="text-xs text-green-600 mt-1">
                {paymentProgress}% ของยอดรวม
              </div>
            </div>
            
            <div className="p-4 bg-gradient-to-r from-red-50 to-red-100 rounded-lg border border-red-200">
              <div className="flex items-center space-x-2 mb-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                <span className="text-sm font-medium text-red-700">ค้างชำระ</span>
              </div>
              <div className="text-2xl font-bold text-red-800">
                {formatCurrency(totalPending)}
              </div>
              <div className="text-xs text-red-600 mt-1">
                {100 - paymentProgress}% ของยอดรวม
              </div>
            </div>
          </div>
          
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>ความคืบหน้าการชำระเงิน</span>
              <span>{paymentProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${paymentProgress}%` }}
              ></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Payment Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>รายละเอียดการชำระเงิน</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Revenue Payments */}
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-700">รายได้จากลูกค้า</span>
              <span className="text-sm font-bold text-green-800">{formatCurrency(stats.totalRevenue)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="text-green-600">ชำระแล้ว: {formatCurrency(stats.totalPaid)}</div>
              <div className="text-red-600">ค้าง: {formatCurrency(stats.pendingAmount)}</div>
            </div>
          </div>

          {/* Company Fee Payments */}
          <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-indigo-700">ค่าบริการบริษัท</span>
              <span className="text-sm font-bold text-indigo-800">{formatCurrency(stats.totalCompanyFee)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="text-green-600">จ่ายแล้ว: {formatCurrency(stats.paidCompanyFee)}</div>
              <div className="text-red-600">ค้าง: {formatCurrency(stats.pendingCompanyFee)}</div>
            </div>
          </div>

          {/* Developer Payments */}
          <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-orange-700">ค่าจ้าง Developer</span>
              <span className="text-sm font-bold text-orange-800">{formatCurrency(stats.totalDeveloperPayments)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="text-green-600">จ่ายแล้ว: {formatCurrency(stats.paidDeveloperPayments)}</div>
              <div className="text-red-600">ค้าง: {formatCurrency(stats.pendingDeveloperPayments)}</div>
            </div>
          </div>

          {/* Other Expenses */}
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-red-700">ค่าใช้จ่ายอื่นๆ</span>
              <span className="text-sm font-bold text-red-800">{formatCurrency(stats.totalOtherExpenses)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="text-green-600">จ่ายแล้ว: {formatCurrency(stats.paidOtherExpenses)}</div>
              <div className="text-red-600">ค้าง: {formatCurrency(stats.pendingOtherExpenses)}</div>
            </div>
          </div>

          {/* Sales Commission */}
          <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-cyan-700">Commission Sale</span>
              <span className="text-sm font-bold text-cyan-800">{formatCurrency(stats.totalSalesCommission)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="text-green-600">จ่ายแล้ว: {formatCurrency(stats.paidSalesCommission)}</div>
              <div className="text-red-600">ค้าง: {formatCurrency(stats.pendingSalesCommission)}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPaymentSummary;
